import {
  HttpRequest,
  HttpResponse,
  HttpResponseMapper,
  ObjectInterceptor,
  HttpRequestInterceptor
} from './interfaces';
import composeResponseMappers from './composeResponseMappers';
import logger from '../log';
import InterceptorType from './InterceptorType';
import copyRequest from './copyRequest';
import { OidFunction } from '@kollective-frontend/ksdk-api';
import { FetchFailed, FetchTimeout, InterceptorDisabled } from './errors';
import { dispatchInterceptorChangeEvent } from '../events/dispatch';
import ContentConfig from '../config/ContentConfig';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents } from '../events';
import { ObjectIdFunction, getObjectIdFunction } from './getObjectIdFunction';
const log = logger.getLogger(__filename);

const ABORT_ERROR = 'AbortError';
const TIMEOUT_ERROR = 'TimeoutError';
const isAbortError = (e?: Error | FetchFailed): boolean => {
  if (!e) {
    return false;
  }
  const rootName = e.name;
  if (rootName == ABORT_ERROR || rootName == TIMEOUT_ERROR) {
    return true;
  }
  const cause = (e as FetchFailed).cause;
  const causeName = (cause as Error)?.name;
  if (causeName == ABORT_ERROR || causeName == TIMEOUT_ERROR) {
    return true;
  }
  return false;
};

export default class InterceptorStack implements HttpRequestInterceptor {
  stack: ObjectInterceptor[];
  responseMapper: HttpResponseMapper | undefined;
  interceptorType = InterceptorType.COMPOSITE;
  createObjectId?: OidFunction;
  config: ContentConfig;
  shouldReport: boolean;
  eventEmitter: EventEmitter;
  objectIdFunction: ObjectIdFunction;

  constructor(
    config: ContentConfig,
    shouldReport?: boolean,
    createObjectId?: OidFunction
  ) {
    this.stack = [];
    this.config = config;
    this.shouldReport = shouldReport || false;
    this.eventEmitter = EventEmitter.getInstance();
    this.objectIdFunction = getObjectIdFunction(
      this.config.app as string,
      createObjectId
    );
  }

  currentPeerConnections(): number {
    return this.stack.reduce((sum, current) => {
      if (typeof current.currentPeerConnections === 'function') {
        return sum + current.currentPeerConnections();
      }
      return sum;
    }, 0);
  }

  putInterceptor(interceptor: ObjectInterceptor): void {
    // unshift puts the value on the front of the array.
    this.stack.unshift(interceptor);
    this.signalInterceptorChange(interceptor, true);
  }

  removeInterceptor(interceptor: ObjectInterceptor): void {
    this.stack = this.stack.filter(i => i !== interceptor);
    this.signalInterceptorChange(interceptor, false);
  }

  signalInterceptorChange(interceptor: ObjectInterceptor, add: boolean): void {
    if (this.shouldReport && interceptor.interceptorName) {
      const name = interceptor.interceptorName;
      dispatchInterceptorChangeEvent({ add, name });
    }
  }

  registerResponseMapper(mapper: HttpResponseMapper): void {
    if (this.responseMapper) {
      this.responseMapper = composeResponseMappers(this.responseMapper, mapper);
    } else {
      this.responseMapper = mapper;
    }
  }

  async handle(request: HttpRequest): Promise<HttpResponse> {
    const toRemove: ObjectInterceptor[] = [];
    const { url, headers, originTimeout } = request;
    const objectId = this.objectIdFunction(url, { headers });
    const req = {
      ...request,
      objectId
    };
    this.eventEmitter.emit(InternalEvents.PENDING_OBJECT, {
      data: { objectId }
    });
    const info = this.stack.reduce((acc, i) => {
      acc.push({ type: i.interceptorType, name: i.interceptorName });
      return acc;
    }, [] as unknown[]);
    const hasTimeout = originTimeout && originTimeout > 0;
    const controller: AbortController | undefined = hasTimeout
      ? new AbortController()
      : undefined;
    if (hasTimeout) {
      setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, originTimeout);
    }
    for (const interceptor of this.stack) {
      try {
        // Return the first available response
        const resp = await interceptor.handle(copyRequest(req, controller));
        log.trace('Handled by', interceptor.interceptorName);
        // SRV-5858: Temporary logging for P1
        const r = this.responseMapper ? this.responseMapper(resp) : resp;
        if (r.status === 412) {
          const { interceptorType: type, interceptorName: name } = interceptor;
          log.warn(`Response: (name:${name},type:${type})`, info, r);
        }
        return Promise.resolve(r);
        //if (this.responseMapper) {
        //  return Promise.resolve(this.responseMapper(resp));
        //} else {
        //  return Promise.resolve(resp);
        //}
      } catch (e) {
        const name = interceptor.interceptorName;
        log.trace('Unable to get a response with', name, e);
        if (e && (e as InterceptorDisabled).interceptorDisabled) {
          toRemove.push(interceptor);
        }
        if (isAbortError(e as unknown as Error | FetchFailed)) {
          return Promise.reject(new FetchTimeout(req, e as Error | string));
        }
      }
    }
    log.debug(
      'All interceptors failed, removing the following interceptors:',
      toRemove
    );
    // if any interceptors are disabled, remove them.
    toRemove.forEach(i => this.removeInterceptor(i));
    // SRV-5858: Temporary logging for P1
    log.warn('Failed to get a response:', { status: 0, request });
    // If we make it here, then it's bad.
    return Promise.reject({
      status: 0,
      request,
      url: request.url
    });
  }
}
